import { effectiveRole } from "../sales/roles";
import { canAccess } from "../providers/commons/canAccess";
import { validateTaskType } from "./taskType";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";

import { DateTimeInput } from "@/components/admin";

import { ReferenceInput } from "@/components/admin/reference-input";

import { SelectInput } from "@/components/admin/select-input";

import { TextInput } from "@/components/admin/text-input";

import { useEffect, useRef } from "react";

import { useFormContext, useWatch } from "react-hook-form";

import { required, useGetIdentity, type Identifier } from "ra-core";

import { contactOptionText } from "../misc/ContactOption";

import { useConfigurationContext } from "../root/ConfigurationContext";

import { TaskContactPicker } from "./TaskContactPicker";
import { DateInput } from "@/components/admin/date-input";
import { isInstallationType } from "./installation";

const employeeOptionText = (choice: {
  first_name?: string;
  last_name?: string;
}) => [choice.first_name, choice.last_name].filter(Boolean).join(" ");

const dealOptionText = (choice: { name?: string }) => choice.name ?? "";

const taskStatuses = [
  {
    value: "new",
    label: "Новая",
  },
  {
    value: "in_progress",
    label: "В работе",
  },
  {
    value: "completed",
    label: "Выполнена",
  },
];

export const TaskFormContent = ({
  selectContact,
  selectedContactIds,
  onSelectedContactIdsChange,
}: {
  selectContact?: boolean;

  selectedContactIds?: Identifier[];

  onSelectedContactIdsChange?: (ids: Identifier[]) => void;
}) => {
  const { taskTypes } = useConfigurationContext();

  const { getValues, setValue } = useFormContext();

  const status = useWatch({
    name: "status",
  });
  const adminOnly = useWatch({ name: "admin_only" });
  const { identity } = useGetIdentity();
  const role = effectiveRole(identity);
  const counterparties = canAccess(role, {
    resource: "counterparties",
    action: "list",
  });
  const canMontage =
    identity?.administrator || ["logistics", "lead_installer"].includes(role);
  const type = useWatch({ name: "type" });
  const installation = isInstallationType(type);
  const periodStart = useWatch({ name: "scheduled_start" });
  const periodEnd = useWatch({ name: "scheduled_end" });
  const previousInstallation = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    if (previousInstallation.current === installation) return;
    previousInstallation.current = installation;
    if (installation) return;
    const end =
      getValues("scheduled_end") ||
      getValues("due_date") ||
      new Date(Date.now() + 3600000).toISOString();
    setValue(
      "scheduled_start",
      getValues("scheduled_start") ||
        new Date(new Date(end).getTime() - 3600000).toISOString(),
    );
    setValue("scheduled_end", end);
  }, [installation, getValues, setValue]);
  useEffect(() => {
    if (!installation)
      setValue("due_date", periodEnd || null, { shouldDirty: true });
  }, [installation, periodEnd, setValue]);
  const teamTask = useWatch({ name: "team_task" });
  const start = useWatch({ name: "installation_start" });
  const choices = taskTypes.filter(
    (t) =>
      t.value !== "none" &&
      !!t.value.trim() &&
      !isInstallationType(t.value) &&
      t.label.toLowerCase() !== "монтаж",
  );
  if (canMontage || installation)
    choices.push({ value: "installation", label: "Монтаж" });
  if (type === "legacy-installation")
    choices.push({ value: type, label: "Монтаж (прежняя задача)" });

  useEffect(() => {
    if (installation) {
      setValue("type", "installation");
      for (const field of [
        "sales_id",
        "due_date",
        "scheduled_start",
        "scheduled_end",
        "contact_id",
      ]) {
        setValue(field, null, { shouldDirty: true });
      }
      setValue("text", "Монтаж", { shouldDirty: true });
      setValue("admin_only", false, { shouldDirty: true });
      if (selectedContactIds?.length) onSelectedContactIdsChange?.([]);
    } else if (!getValues("sales_id") && !getValues("team_task")) {
      setValue("sales_id", identity?.id);

      if (getValues("text") === "Монтаж") setValue("text", "");
    }
  }, [
    installation,
    setValue,
    getValues,
    identity?.id,
    selectedContactIds,
    onSelectedContactIdsChange,
  ]);

  useEffect(() => {
    if (!installation && !identity?.administrator && teamTask)
      setValue("team_task", false, { shouldDirty: true });
    if (!installation && teamTask && identity?.administrator) {
      setValue("sales_id", null, { shouldDirty: true });
      setValue("admin_only", false, { shouldDirty: true });
    }
  }, [teamTask, installation, setValue, identity?.administrator]);

  useEffect(() => {
    const doneDate = getValues("done_date");

    if (status === "completed" && !doneDate) {
      setValue("done_date", new Date().toISOString(), {
        shouldDirty: true,
      });
    }

    if (status !== "completed" && doneDate) {
      setValue("done_date", null, {
        shouldDirty: true,
      });
    }
  }, [status, getValues, setValue]);

  useEffect(() => {
    if (selectedContactIds === undefined) {
      return;
    }

    setValue("contact_id", selectedContactIds[0] ?? null, {
      shouldDirty: true,
    });
  }, [selectedContactIds, setValue]);

  const useMultiContactPicker =
    selectedContactIds !== undefined && onSelectedContactIdsChange != null;

  return (
    <div className="flex flex-col gap-4">
      <SelectInput
        source="type"
        label="Тип задачи"
        choices={choices}
        optionText="label"
        optionValue="value"
        helperText={false}
        validate={[required(), validateTaskType]}
      />

      {!installation && (
        <SelectInput
          source="status"
          label="Статус"
          choices={taskStatuses}
          optionText="label"
          optionValue="value"
          defaultValue="new"
          helperText={false}
          validate={required()}
        />
      )}

      {!installation && identity?.administrator && (
        <label className="flex cursor-pointer items-center gap-3 rounded-xl border p-3 text-sm">
          <input
            type="checkbox"
            className="size-4 accent-primary"
            checked={!!teamTask}
            onChange={(event) => {
              setValue("team_task", event.target.checked, {
                shouldDirty: true,
              });
              setValue("sales_id", event.target.checked ? null : identity.id, {
                shouldDirty: true,
              });
            }}
          />
          Для всей команды
        </label>
      )}

      {!installation && !teamTask && (
        <ReferenceInput
          source="sales_id"
          reference="sales"
          filter={{
            "disabled@neq": true,
            ...(adminOnly || identity?.hidden_from_team
              ? { administrator: true }
              : {}),
          }}
          sort={{
            field: "last_name",
            order: "ASC",
          }}
        >
          <AutocompleteInput
            label="Ответственный"
            optionText={employeeOptionText}
            helperText={false}
            validate={required()}
          />
        </ReferenceInput>
      )}

      <ReferenceInput
        source="deal_id"
        reference="project_lookup"
        sort={{
          field: "name",
          order: "ASC",
        }}
      >
        <AutocompleteInput
          label="Заказ / проект"
          modal
          optionText={dealOptionText}
          helperText={false}
          clearable
          validate={installation ? required() : undefined}
        />
      </ReferenceInput>

      {!installation &&
        counterparties &&
        (useMultiContactPicker ? (
          <TaskContactPicker
            value={selectedContactIds}
            onChange={onSelectedContactIdsChange}
          />
        ) : (
          selectContact && (
            <ReferenceInput source="contact_id" reference="contacts_summary">
              <AutocompleteInput
                label="Клиент / контакт"
                optionText={contactOptionText}
                helperText={false}
                clearable
              />
            </ReferenceInput>
          )
        ))}

      {installation ? (
        <>
          <p className="text-sm text-muted-foreground">
            Монтаж доступен руководителю, админу, логисту и главному монтажнику.
            Последний день входит в период монтажа.
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <DateInput
              source="installation_start"
              label="Первый день монтажа"
              helperText={false}
              validate={required()}
            />
            <DateInput
              source="installation_end"
              label="Последний день монтажа"
              helperText={false}
              validate={[
                required(),
                (value: string) =>
                  value && start && value < start
                    ? "Окончание не может быть раньше начала"
                    : undefined,
              ]}
            />
          </div>
        </>
      ) : (
        <>
          <TextInput
            source="text"
            label="Описание задачи"
            multiline
            rows={3}
            helperText={false}
            validate={required()}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <DateTimeInput
              source="scheduled_start"
              label="Начало задачи"
              helperText={false}
              validate={required()}
            />
            <DateTimeInput
              source="scheduled_end"
              label="Окончание задачи"
              helperText={false}
              validate={[
                required(),
                (value: string) =>
                  value &&
                  periodStart &&
                  new Date(value) <= new Date(periodStart)
                    ? "Окончание должно быть позже начала"
                    : undefined,
              ]}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Окончание — дедлайн. Задача появится в календаре во все дни своего
            периода.
          </p>
        </>
      )}
    </div>
  );
};
